/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useEffect, useState, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { InputLabel, Autocomplete } from "@mui/material";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDDropzone from "components/MDDropzone";
import MDAvatar from "components/MDAvatar";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate, useParams } from "react-router-dom";

import CrudService from "services/cruds-service";
import organizationsService from "services/organizations.service";
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [image, setImage] = useState("");
  const [fileState, setFileState] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [user, setUser] = useState({
    id: "",
    name: "",
    email: "",
    role: "",
    organization: "",
  });
  const [value, setValue] = useState({});

  const [error, setError] = useState({
    name: false,
    email: false,
    role: false,
    error: false,
    textError: "",
  });

  const [organizations, setOrganizations] = useState([]);
  const [selOrg, setSelOrg] = useState(null);

  const ability = useAbility(AbilityContext);

  useEffect(() => {
    (async () => {
      try {
        const response = await CrudService.getRoles();
        setRoles(response.data);
      } catch (err) {
        console.error(err);
        return null;
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await CrudService.getUser(id);
        const getData = response.data;
        const roleData = await CrudService.getRole(getData.relationships.roles.data[0].id);
        const role = roleData.data
        if (ability.can("create", "organizations")) {
          const orgsResponse = await organizationsService.getOrganizations();
          const mapped = orgsResponse.map((m) => ({
            id: m._id,
            label: m.name,
          }));
          if (getData.attributes.organization) {
            const selectedOrg = getData.relationships.organizations.data[0];
            setSelOrg(selectedOrg.id ? selectedOrg : null);
          }
          setOrganizations(mapped);
        }
        setUser({
          id: getData.id,
          name: getData.attributes.name,
          email: getData.attributes.email,
          role: getData.relationships.roles.data[0].id,
        });
        let profileImage = "";
        try {
          profileImage = await CrudService.getImage(getData.attributes.profile_image);
        } catch (err) {
          profileImage = getData.attributes.profile_image;
        }
        setImage(profileImage);
        setValue(role);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (roles && roles.length > 0) {
      const role = roles.find((role) => role.id === user.role);
      setValue(role);
    }
  }, [roles]);

  const changeHandler = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  // Whenever an image is added to the dropzone, it is stored in newImage
  const onImageAdd = (file) => {
    console.log(file);
    setNewImage(file);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (user.name.trim().length === 0) {
      setError({
        email: false,
        role: false,
        name: true,
        textError: "The name cannot be empty",
      });
      return;
    }

    if (user.email.trim().length === 0 || !user.email.trim().match(mailFormat)) {
      setError({
        role: false,
        name: false,
        email: true,
        textError: "The email is not valid",
      });
      return;
    }

    if (value.id && value.id === "") {
      setError({
        name: false,
        email: false,
        role: false,
        password: false,
        confirm: false,
        role: true,
        textError: "Role is required",
      });
      return;
    }

    try {
      let imagePath;
      if (newImage) {
        const formData = {
          filename: newImage.upload.filename,
          dataUrl: newImage.dataURL,
          folderpath: user.name,
        }
        imagePath = await CrudService.setUserImage(formData);
      } else {
        imagePath = { s3Key: user.profile_image };
      }
      const newUser = {
        data: {
          id: user.id.toString(),
          type: "users",
          attributes: {
            name: user.name,
            email: user.email,
            profile_image: imagePath.s3Key,
            organization: selOrg ? selOrg.id : "",
          },
          relationships: {
            roles: {
              data: [
                {
                  id: value.id ? value.id.toString() : user.role.toString(),
                  type: "roles",
                },
              ],
            },
          },
        },
      };

      console.log("New User Data:",newUser);

      try {
        const res = await CrudService.updateUser(newUser, newUser.data.id);
        navigate("/examples-api/user-management", {
          state: { value: true, text: "The user was sucesfully updated" },
        });
      } catch (err) {
        if (err.hasOwnProperty("errors")) {
          setError({ ...error, error: true, textError: err.errors[0].detail });
        }
        console.error(err);
      }
    } catch (err) {
      setError({ ...error, error: true, textError: err.message });
      return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={user.name} />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Edit User
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                This form is used to modify a user's details.
              </MDTypography>
            </MDBox>
            <Card>
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Name"
                        placeholder="Alec"
                        name="name"
                        value={user.name}
                        onChange={changeHandler}
                        error={error.name}
                      />
                      {error.name && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Email"
                        placeholder="example@email.com"
                        inputProps={{ type: "email" }}
                        name="email"
                        value={user.email}
                        onChange={changeHandler}
                        error={error.email}
                      />
                      {error.email && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>
                  <MDBox display="flex" flexDirection="column" fullWidth>
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      fullWidth
                    >
                      <Grid container spacing={3} mt={4}>
                        <Grid item xs={6} md={4}>
                          <MDBox display="flex" flexDirection="column">
                            <InputLabel id="demo-simple-select-label">Profile Image</InputLabel>
                            {useMemo(
                              () => (
                                <MDDropzone
                                  options={{ addRemoveLinks: true, maxFiles: 1, acceptedFiles: 'image/*' }}
                                  onFileAdd={onImageAdd}
                                  acceptedFiles="image/*"
                                />
                              ),
                              []
                            )}
                          </MDBox>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          {image && (
                            <MDBox ml={4} mt={4}>
                              <MDAvatar
                                src={imageUrl ?? image}
                                alt="profile-image"
                                size="xxl"
                                shadow="sm"
                              />
                            </MDBox>
                          )}
                        </Grid>
                        <Grid items xs={12} md={4}>
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            fullWidth
                            marginBottom="1rem"
                            marginTop="3rem"
                          >
                            <MDBox>
                              <Autocomplete
                                defaultValue={null}
                                options={roles}
                                getOptionLabel={(option) => {
                                  if (option.data) {
                                    if (option.data.attributes) {
                                      if (option.data.attributes.name) return option.data.attributes.name;
                                    }
                                  } else {
                                    if (option.attributes) {
                                      if (option.attributes.name) return option.attributes.name;
                                    }
                                  }
                                  return "";
                                }
                                }
                                value={value ?? null}
                                onChange={(event, newValue) => {
                                  setValue(newValue);
                                }}
                                renderInput={(params) => (
                                  <FormField {...params} label="Role" InputLabelProps={{ shrink: true }} />
                                )}
                              />
                              {error.role && (
                                <MDTypography variant="caption" color="error" fontWeight="light" pl={4}>
                                  {error.textError}
                                </MDTypography>
                              )}
                            </MDBox>
                            {ability.can("create", "organizations") && organizations && (
                              <MDBox mt={3}>
                                <Autocomplete
                                  defaultValue={selOrg}
                                  options={organizations}
                                  getOptionLabel={(option) => (option ? option.label : "")}
                                  value={selOrg ?? ""}
                                  onChange={(event, newValue) => {
                                    setSelOrg(newValue);
                                  }}
                                  renderInput={(params) => (
                                    <FormField {...params} label="Organization" InputLabelProps={{ shrink: true }} />
                                  )}
                                />
                              </MDBox>
                            )}
                          </MDBox>
                        </Grid>
                      </Grid>

                    </MDBox>
                  </MDBox>
                  {error.error && (
                    <MDTypography variant="caption" color="error" fontWeight="light" pt={2}>
                      {error.textError}
                    </MDTypography>
                  )}
                  <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">
                    <MDBox mx={2}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/examples-api/user-management", {
                            state: { value: false, text: "" },
                          })
                        }
                      >
                        Back
                      </MDButton>
                    </MDBox>
                    <MDButton variant="gradient" color="dark" size="small" type="submit">
                      Save
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default EditUser;
